'use client';

import { useId, useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import { useDictionary } from '@/components/providers';
import { interpolate } from '@/lib/i18n/interpolate';
import { type CONSENT_CATEGORIES } from './consent.constants';
import type { ConsentService } from './consent.types';
import { ConsentServiceItem } from './ConsentServiceItem';
import { ConsentToggle } from './ConsentToggle';
import { ConsentServiceList } from './ConsentServiceList';

/** Project palette — the only lines that differ between the sibling projects. */
const CLS = {
    label: 'flex items-center gap-2 text-sm font-medium text-title',
    badge: 'text-xs font-normal text-copy-muted',
    description: 'mt-1 text-sm text-copy-2',
    disclosure: 'text-copy-muted hover:text-title',
    serviceDivider: 'divide-hairline border-hairline',
};

type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

type Props = {
    category: ConsentCategory;
    /** Every service registered for this category. */
    services: ConsentService[];
    checked: boolean;
    isServiceChecked: (service: ConsentService) => boolean;
    onCategoryChange: (checked: boolean) => void;
    onServiceChange: (service: ConsentService, checked: boolean) => void;
};

/**
 * One consent category: a master switch over its services, plus a disclosure
 * that reveals every service with its own description and switch.
 */
export function ConsentCategoryItem({
    category,
    services,
    checked,
    isServiceChecked,
    onCategoryChange,
    onServiceChange,
}: Props) {
    const { consent } = useDictionary();
    const [open, setOpen] = useState(false);
    const baseId = useId();
    const toggleId = `${baseId}-toggle`;
    const listId = `${baseId}-services`;

    const banner = consent.banner;
    // See ConsentServiceItem: a missing entry shows the key rather than hiding a
    // category the visitor is deciding about.
    const copy = consent.categories[category.key] ?? { label: category.key, description: '' };

    return (
        <li className="py-4">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <label htmlFor={toggleId} className={CLS.label}>
                        {copy.label}
                        {category.required && <span className={CLS.badge}>({banner.alwaysActive})</span>}
                    </label>
                    {copy.description ? <p className={CLS.description}>{copy.description}</p> : null}

                    {services.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setOpen((prev) => !prev)}
                            aria-expanded={open}
                            aria-controls={listId}
                            aria-label={open ? banner.hideServices : banner.showServices}
                            className={cn(
                                'mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs transition-colors',
                                CLS.disclosure,
                            )}
                        >
                            {banner.servicesLabel} ({services.length})
                            <UiIcon
                                name="chevron-down"
                                className={cn('size-3.5 transition-transform', open && 'rotate-180')}
                                aria-hidden="true"
                            />
                        </button>
                    )}
                </div>

                <ConsentToggle
                    id={toggleId}
                    checked={checked}
                    disabled={category.required}
                    label={interpolate(banner.allowToggle, { name: copy.label })}
                    onChange={onCategoryChange}
                />
            </div>

            {services.length > 0 && (
                <ConsentServiceList id={listId} hidden={!open} className={CLS.serviceDivider}>
                    {services.map((service) => (
                        <ConsentServiceItem
                            key={service.id}
                            service={service}
                            checked={isServiceChecked(service)}
                            disabled={category.required}
                            onChange={(next) => onServiceChange(service, next)}
                            toggleId={`${baseId}-${service.id}`}
                        />
                    ))}
                </ConsentServiceList>
            )}
        </li>
    );
}
